import { useState, useRef, useEffect } from "react";

const serviceOptions = [
  "Asesoramiento y Consultoría en Marketing",
  "Marketing Digital y Estrategias de Crecimiento",
  "Creación de Sitios Web con IA",
  "Gestión de Redes Sociales",
  "Dashboards y Análisis de Datos",
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    empresa: "",
    servicio: "",
    mensaje: "",
  });
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = "opacity 0.8s ease, transform 0.8s ease";
          el.style.opacity = "1";
          el.style.transform = "translateY(0)";
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    el.style.opacity = "0";
    el.style.transform = "translateY(20px)";
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "mensaje" && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.servicio || !formData.mensaje) return;
    setStatus("sending");

    const params = new URLSearchParams();
    params.append("nombre", formData.nombre);
    params.append("email", formData.email);
    if (formData.empresa) params.append("empresa", formData.empresa);
    params.append("servicio", formData.servicio);
    params.append("mensaje", formData.mensaje);

    try {
      await fetch("https://readdy.ai/api/form/d71d7puuimmp87kg1fe0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setStatus("success");
      setFormData({ nombre: "", email: "", empresa: "", servicio: "", mensaje: "" });
    } catch {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#C9A227] text-white placeholder-gray-600 rounded-md px-5 py-3.5 text-sm outline-none transition-colors duration-200";

  return (
    <section id="contacto" className="py-32" style={{ background: "#080808" }}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16">

        {/* Top Visual Banner */}
        <div className="relative rounded-2xl overflow-hidden mb-20" style={{ height: "320px" }}>
          <img
            src="https://readdy.ai/api/search-image?query=dark%20luxury%20digital%20marketing%20office%20premium%20corporate%20atmosphere%20elegant%20meeting%20room%20golden%20accent%20lighting%20sophisticated%20technology%20strategy%20team%20professional&width=1400&height=320&seq=contact-bg-1&orientation=landscape"
            alt="Contact Banner"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/60"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
            <p className="text-gray-400 text-sm tracking-widest uppercase mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Hablemos de tu proyecto
            </p>
            <h2
              className="text-white text-4xl lg:text-5xl font-bold"
              style={{ fontFamily: "'Cormorant Garamond', serif", lineHeight: "1.2" }}
            >
              Transformamos tu{" "}
              <em className="text-[#C9A227] not-italic">visión</em> en estrategia
            </h2>
          </div>
        </div>

        {/* Form Area */}
        <div ref={sectionRef} className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left: Info */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-[#C9A227] text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                / CONTACTO
              </span>
            </div>
            <h3
              className="text-white text-3xl lg:text-4xl font-bold mb-6 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Solicita tu consultoría estratégica
            </h3>
            <p className="text-gray-400 text-base leading-relaxed mb-10" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              Nuestro equipo analizará tu caso y te contactará en breve con una propuesta personalizada, sin compromiso.
            </p>

            {/* Trust Points */}
            <div className="flex flex-col gap-5">
              {[
                { icon: "ri-time-line", text: "Respuesta en menos de 24 horas" },
                { icon: "ri-shield-check-line", text: "Información tratada con total confidencialidad" },
                { icon: "ri-customer-service-2-line", text: "Primera consultoría sin coste" },
              ].map((point) => (
                <div key={point.text} className="flex items-center gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C9A227]/10 border border-[#C9A227]/20 flex-shrink-0">
                    <i className={`${point.icon} text-[#C9A227] text-base`}></i>
                  </div>
                  <p className="text-gray-300 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {point.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Decorative image */}
            <div className="mt-12 rounded-xl overflow-hidden" style={{ height: "200px" }}>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20team%20handshake%20agreement%20luxury%20dark%20office%20golden%20warm%20light%20premium%20corporate%20partner%20trust%20minimalist%20elegant&width=600&height=200&seq=contact-left-1&orientation=landscape"
                alt="Imperial Digital team"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div className="rounded-2xl p-8 lg:p-10 border border-[#1a1a1a]" style={{ background: "#0f0f0f" }}>
            {status === "success" ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A227]/10 border border-[#C9A227]/30 mb-6">
                  <i className="ri-check-line text-[#C9A227] text-3xl"></i>
                </div>
                <h3
                  className="text-white text-2xl font-bold mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  ¡Mensaje enviado!
                </h3>
                <p className="text-gray-400 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Nuestro equipo analizará tu caso y se pondrá en contacto contigo en breve.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-8 border border-[#C9A227]/40 text-[#C9A227] text-sm font-semibold px-8 py-3 rounded-md hover:bg-[#C9A227]/10 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                  <label className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Nombre *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Tu nombre completo"
                    required
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "14px" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "14px" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Empresa <span className="text-gray-600 normal-case font-normal">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleChange}
                    placeholder="Nombre de tu empresa"
                    className={inputClass}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "14px" }}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Servicio de interés *
                  </label>
                  <select
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    required
                    className={`${inputClass} cursor-pointer appearance-none`}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "14px" }}
                  >
                    <option value="" disabled>Selecciona un servicio</option>
                    {serviceOptions.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#1a1a1a] text-white">
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-2 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Mensaje *
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="Cuéntanos sobre tu proyecto o necesidad..."
                    required
                    rows={4}
                    maxLength={500}
                    className={`${inputClass} resize-none`}
                    style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "14px" }}
                  />
                  <div className="text-right mt-1 text-gray-600 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {formData.mensaje.length}/500
                  </div>
                </div>

                {status === "error" && (
                  <p className="text-red-400 text-sm text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    Hubo un error al enviar. Por favor, inténtalo de nuevo.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="bg-[#C9A227] hover:bg-[#b8911e] disabled:opacity-60 text-black font-bold py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer text-sm tracking-wider uppercase mt-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {status === "sending" ? "Enviando..." : "Solicitar información"}
                </button>

                <p className="text-gray-600 text-xs text-center" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Nuestro equipo analizará tu caso y te contactará en breve.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
